import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  output,
  ViewChild,
  input,
  effect,
} from '@angular/core';

import { EditorService } from '@core/services/editor.service';
import EditorJS from '@editorjs/editorjs';

@Component({
  selector: 'app-editor',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="editorjs-container">
      <div #editorContainer></div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        padding-left: 60px;
        padding-right: 50px;
      }
      @media (max-width: 650px) {
        :host {
          padding: 0 20px;
        }
      }
    `,
  ],
})
export class EditorComponent implements AfterViewInit, OnDestroy {
  private readonly editorService = inject(EditorService);
  private readonly destroyRef = inject(DestroyRef);
  private editor: EditorJS | null = null;
  private isInitialized = false;
  private isUpdating = false;
  private holderId: string = '';
  private currentDataJson: string = '';
  private skipNextChange = false; // Флаг для пропуска следующего onChange
  private lastBlocksSignature: string = ''; // Сигнатура блоков для отсечки ложных onChange

  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef;

  data = input<any>({});
  readOnly = input<boolean>(false);
  placeholder = input<string>('Добавьте описание задачи...');
  autofocus = input<boolean>(false);
  imageUploadCallback = input<(file: File) => Promise<{ success: boolean; file: { url: string } }>>();

  editorReady = output<EditorJS>();
  dataChanged = output<any>();
  editorSaved = output<any>();
  focus = output<void>();
  blur = output<void>();

  constructor() {
    // Effect для обработки изменений входных параметров
    effect(() => {
      // Получаем текущие значения сигналов для отслеживания изменений
      const currentData = this.data();

      // Пропускаем, если редактор не инициализирован или обновляется
      if (!this.isInitialized || this.isUpdating) {
        return;
      }

      // Преобразуем данные в JSON для сравнения
      let newDataJson = '';
      try {
        if (typeof currentData === 'string') {
          // Если это уже строка, используем её напрямую
          newDataJson = currentData;
        } else {
          // Иначе преобразуем объект в JSON
          newDataJson = JSON.stringify(currentData);
        }
      } catch (e) {
        console.error('Ошибка при сериализации данных:', e);
        newDataJson = '';
      }

      // Сравниваем текущий JSON с новым, чтобы избежать циклических обновлений
      if (newDataJson && newDataJson !== this.currentDataJson) {
        this.currentDataJson = newDataJson;
        this.skipNextChange = true; // Устанавливаем флаг перед обновлением
        this.updateEditorData(currentData);
      }
    });
  }

  /**
   * Инициализирует EditorJS после отрисовки представления
   */
  ngAfterViewInit(): void {
    // Устанавливаем уникальный ID для контейнера редактора только один раз
    this.holderId = `editor-js-${Date.now()}`;
    this.editorContainer.nativeElement.id = this.holderId;
    this.initEditor();
  }

  /**
   * Освобождает ресурсы при уничтожении компонента
   */
  ngOnDestroy(): void {
    this.destroyEditor();
  }

  /**
   * Декодирует JSON из строки или возвращает исходные данные
   */
  private parseJsonData(data: any): any {
    if (!data) {
      return {};
    }

    // Если данные уже в формате объекта и содержат blocks, возвращаем как есть
    if (typeof data === 'object' && data.blocks) {
      return data;
    }

    // Если данные в формате строки, пытаемся распарсить JSON
    if (typeof data === 'string') {
      try {
        const parsedData = JSON.parse(data);
        return parsedData;
      } catch (e) {
        // Если строка не является валидным JSON, создаем блок с этой строкой
        return {
          blocks: [
            {
              type: 'paragraph',
              data: {
                text: data,
              },
            },
          ],
        };
      }
    }

    // Если ничего не подошло, возвращаем пустой объект
    return {};
  }

  /**
   * Обновляет данные редактора без его пересоздания
   */
  private async updateEditorData(newData: any): Promise<void> {
    if (!this.editor || !this.isInitialized) {
      return;
    }

    this.isUpdating = true;

    try {
      // Декодируем данные из JSON, если они в строковом формате
      const parsedData = this.parseJsonData(newData);
      await this.editor.blocks.clear();

      if (parsedData.blocks && parsedData.blocks.length) {
        // Затем рендерим новые блоки
        await this.editor.blocks.render({
          blocks: parsedData.blocks,
        });
      }

      // Обновляем сигнатуру блоков, чтобы последующий onChange не был ложно-срабатывающим
      try {
        const saved = await this.editor.save();
        this.lastBlocksSignature = this.getBlocksSignature(saved);
      } catch {
        /* noop */
      }
    } catch (error) {
      console.error('EditorComponent updateEditorData: ошибка при обновлении данных', error);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Возвращает сигнатуру блоков для сравнения, без волатильных полей (time, version)
   */
  private getBlocksSignature(data: any): string {
    if (!data || !data.blocks) {
      return '';
    }
    try {
      return JSON.stringify(data.blocks);
    } catch {
      return '';
    }
  }

  /**
   * Инициализирует редактор с текущими настройками
   */
  private initEditor(): void {
    // Получаем исходные данные и сохраняем их как текущие
    const initialData = this.data();

    try {
      if (typeof initialData === 'string') {
        this.currentDataJson = initialData;
      } else {
        this.currentDataJson = JSON.stringify(initialData);
      }
    } catch (e) {
      console.error('EditorComponent initEditor: ошибка при сериализации данных', e);
      this.currentDataJson = '';
    }

    // Декодируем данные из JSON, если они в строковом формате
    const parsedData = this.parseJsonData(initialData);
    // Инициализируем редактор с исходными данными и передаем callback для изменений
    this.editor = this.editorService.initialize(
      this.holderId,
      parsedData, // Используем распарсенные данные
      this.readOnly(),
      this.placeholder(),
      this.imageUploadCallback ? this.imageUploadCallback() : this.handleImageUpload.bind(this),
      // Передаем callback для обработки изменений
      this.handleEditorChange.bind(this)
    );

    // Отслеживаем событие готовности редактора
    this.editor.isReady.then(async () => {
      this.isInitialized = true;

      // Захватываем сигнатуру блоков после рендеринга, чтобы спонтанные
      // onChange сразу после инициализации не помечали данные как изменённые
      try {
        const initialSaved = await this.editor!.save();
        this.lastBlocksSignature = this.getBlocksSignature(initialSaved);
      } catch {
        /* noop */
      }

      this.editorReady.emit(this.editor);

      // Добавляем обработчики фокуса
      const editorElement = this.editorContainer.nativeElement;
      editorElement.addEventListener('focusin', () => {
        this.focus.emit();
      });

      editorElement.addEventListener('focusout', () => {
        this.blur.emit();
      });
    });
  }

  /**
   * Уничтожает текущий экземпляр редактора
   */
  private destroyEditor(): void {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
      this.isInitialized = false;
    }
  }

  /**
   * Обрабатывает изменения в редакторе
   */
  private async handleEditorChange(): Promise<void> {
    if (!this.editor || this.isUpdating) {
      return;
    }

    // Если установлен флаг пропуска, сбрасываем его и выходим
    if (this.skipNextChange) {
      this.skipNextChange = false;
      return;
    }

    try {
      const savedData = await this.editor.save();

      // Сравниваем сигнатуру блоков с предыдущей. Если ничего не изменилось,
      // не эмитим событие — это защита от ложных onChange (например, сразу
      // после инициализации, при загрузке плагинов и т.п.)
      const signature = this.getBlocksSignature(savedData);
      if (signature === this.lastBlocksSignature) {
        return;
      }
      this.lastBlocksSignature = signature;

      // Преобразуем данные в JSON-строку и сохраняем как текущие
      const jsonString = JSON.stringify(savedData);
      this.currentDataJson = jsonString;

      // Emit события только если редактор не в процессе обновления
      this.dataChanged.emit(jsonString);
    } catch (error) {
      console.error('EditorComponent handleEditorChange: ошибка при сохранении данных', error);
    }
  }

  /**
   * Обработчик для загрузки изображений
   * @param file Загружаемый файл
   */
  private async handleImageUpload(file: File): Promise<{ success: boolean; file: { url: string } }> {
    // Здесь должна быть реализация загрузки файла
    // TODO: Заменить на реальную реализацию загрузки
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = event => {
        resolve({
          success: true,
          file: {
            url: event.target?.result as string,
          },
        });
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Публичный метод для сохранения содержимого редактора
   */
  public async save(): Promise<any> {
    if (this.editor) {
      try {
        const savedData = await this.editor.save();

        // Преобразуем данные в JSON-строку перед отправкой
        const jsonString = JSON.stringify(savedData);
        this.currentDataJson = jsonString;
        this.editorSaved.emit(jsonString);
        return jsonString;
      } catch (error) {
        console.error('Ошибка при сохранении данных редактора:', error);
        throw error;
      }
    }
    return null;
  }
}
